import { Privacies } from "database";
import {
  TicketStatus,
  getTicketSaleStatus,
} from "@/helper/getTicketSaleStatus";

/**
 * Flattened ticket shape from event.tickets (see flattenTicketWithBookableItem).
 * Used only to decide skip-ticket-list on the client from context data.
 */
export type EventPageTicketForSkip = {
  id: string;
  deletedAt?: Date | string | null;
  privacy: string;
  token?: string | null;
  saleStartDate: Date | string | null;
  saleEndDate: Date | string | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  quantity?: number | null;
  showWhenSoldOut?: boolean | null;
  bookableItem?: { quantity?: number | null };
};

/** Skip only while the ticket is strictly on sale (same notion as getTicketSaleStatus ON_SALE). */
function isStrictlyOnSale(ticket: EventPageTicketForSkip): boolean {
  if (!ticket.saleStartDate || !ticket.saleEndDate) return false;
  return (
    getTicketSaleStatus({
      saleStartDate: new Date(ticket.saleStartDate),
      saleEndDate: new Date(ticket.saleEndDate),
    } as Parameters<typeof getTicketSaleStatus>[0]) === TicketStatus.ON_SALE
  );
}

/**
 * Without order inventory on the event wall, we only exclude obvious “zero cap” sold-out.
 * Full parity with checkout inventory requires server data.
 */
function passesSoldOutDisplayRule(ticket: EventPageTicketForSkip): boolean {
  const qty = ticket.quantity ?? ticket.bookableItem?.quantity ?? undefined;
  if (qty === 0 && !ticket.showWhenSoldOut) return false;
  return true;
}

/**
 * Returns the single ticket to auto-select when the user has no real choice:
 * exactly one purchasable option under normal public / link rules and min/max per order = 1.
 */
export function getSingleSkippableCheckoutTicket(
  tickets: EventPageTicketForSkip[] | undefined,
  options: { token: string | null }
): EventPageTicketForSkip | null {
  const { token } = options;
  const list = tickets ?? [];

  if (token) {
    const match = list.find((x) => !x.deletedAt && x.token === token);
    if (!match) return null;
    if (!isStrictlyOnSale(match) || !passesSoldOutDisplayRule(match)) {
      return null;
    }
    if (match.minAmount !== 1 || match.maxAmount !== 1) return null;
    return match;
  }

  const eligible = list.filter(
    (x) =>
      !x.deletedAt &&
      x.privacy === Privacies.PUBLIC &&
      x.token == null &&
      isStrictlyOnSale(x) &&
      passesSoldOutDisplayRule(x)
  );

  if (eligible.length !== 1) return null;
  const only = eligible[0];
  if (only.minAmount !== 1 || only.maxAmount !== 1) return null;
  return only;
}
